import React, { useState, useEffect } from 'react';
import { getTrainings, createTraining } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  GraduationCap, 
  Plus, 
  Clock,
  Play,
  ExternalLink
} from 'lucide-react';

const TRAINING_CATEGORIES = ['Safety', 'Compliance', 'Leadership', 'Emergency Response', 'HR', 'Technical', 'Other'];

export default function Training() {
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const { isOrgAdmin } = useAuth();

  const [newTraining, setNewTraining] = useState({
    title: '',
    category: '',
    description: '',
    content_url: '',
    duration_minutes: 30,
  });

  useEffect(() => {
    fetchTrainings();
  }, [filterCategory]);

  const fetchTrainings = async () => {
    try {
      const params = filterCategory && filterCategory !== 'all' ? { category: filterCategory } : {};
      const response = await getTrainings(params);
      setTrainings(response.data);
    } catch (error) {
      toast.error('Failed to fetch trainings');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTraining = async (e) => {
    e.preventDefault();
    try {
      await createTraining({
        ...newTraining,
        duration_minutes: parseInt(newTraining.duration_minutes),
      });
      toast.success('Training created');
      setShowCreateDialog(false);
      setNewTraining({
        title: '',
        category: '',
        description: '',
        content_url: '',
        duration_minutes: 30,
      });
      fetchTrainings();
    } catch (error) {
      toast.error('Failed to create training');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="training-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold font-['Barlow_Condensed'] uppercase tracking-tight">
            Training
          </h1>
          <p className="text-muted-foreground mt-1">
            Safety training and behavior change resources
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {TRAINING_CATEGORIES.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isOrgAdmin() && (
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button data-testid="create-training-btn" className="btn-primary-glow">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Training
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-['Barlow_Condensed'] uppercase tracking-tight">
                    Create Training Module
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateTraining} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Training Title</Label>
                    <Input
                      value={newTraining.title}
                      onChange={(e) => setNewTraining({...newTraining, title: e.target.value})}
                      placeholder="Safety Fundamentals"
                      required
                      className="bg-secondary/50"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select 
                        value={newTraining.category} 
                        onValueChange={(v) => setNewTraining({...newTraining, category: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {TRAINING_CATEGORIES.map(cat => (
                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Duration (minutes)</Label>
                      <Input
                        type="number"
                        value={newTraining.duration_minutes}
                        onChange={(e) => setNewTraining({...newTraining, duration_minutes: e.target.value})}
                        className="bg-secondary/50"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Content URL (optional)</Label>
                    <Input
                      value={newTraining.content_url}
                      onChange={(e) => setNewTraining({...newTraining, content_url: e.target.value})}
                      placeholder="https://..."
                      className="bg-secondary/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={newTraining.description}
                      onChange={(e) => setNewTraining({...newTraining, description: e.target.value})}
                      placeholder="Training description..."
                      rows={4}
                      required
                      className="bg-secondary/50"
                    />
                  </div>
                  <Button type="submit" className="w-full btn-primary-glow">
                    Create Training
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Training Grid */}
      {trainings.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {trainings.map((training) => (
            <Card key={training.id} className="bg-card border-border card-hover">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <Badge variant="outline" className="text-xs">
                    {training.category}
                  </Badge>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Clock className="w-4 h-4" />
                    {training.duration_minutes} min
                  </div>
                </div>
                <CardTitle className="text-lg mt-2">{training.title}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground line-clamp-3">
                  {training.description}
                </p>
                {training.content_url ? (
                  <a 
                    href={training.content_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <Button className="w-full btn-primary-glow">
                      <Play className="w-4 h-4 mr-2" />
                      Start Training
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                ) : (
                  <Button variant="outline" className="w-full" disabled>
                    <Play className="w-4 h-4 mr-2" />
                    Content Coming Soon
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-card border-border">
          <CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <GraduationCap className="w-12 h-12 mb-4 opacity-50" />
            <p>No training modules found</p>
            {isOrgAdmin() && (
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setShowCreateDialog(true)}
              >
                Create First Training
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
